import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { adminApi } from "../../api/admin";
import { useAuth } from "../../hooks/useAuth";
import { formatApiError } from "../../lib/errors";

export default function UserDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const [data, setData] = useState(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [pendingRole, setPendingRole] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    adminApi.getUserDetail(id).then((resp) => setData(resp.data));
  }, [id]);

  if (!data) return <p className="text-[#A1A1AA]">Loading...</p>;
  const { user, orders, licences } = data;
  const isSelf = me?.id === user._id;

  const requestRoleToggle = () => {
    if (isSelf) return;
    setPendingRole(user.role === "admin" ? "user" : "admin");
    setShowRoleModal(true);
    setError("");
  };

  const confirmRoleChange = async () => {
    try {
      await adminApi.updateUserRole(id, pendingRole);
      setData({ ...data, user: { ...user, role: pendingRole } });
      setShowRoleModal(false);
    } catch (e) {
      setError(formatApiError(e, "Role change failed"));
    }
  };

  const confirmDelete = async () => {
    if (confirmEmail !== user.email) return;
    try {
      await adminApi.deleteUser(id);
      navigate("/admin/users");
    } catch (e) {
      setError(formatApiError(e, "Delete failed"));
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="font-clash text-2xl font-bold text-white">{user.name}</h1>

      <div className="feature-card rounded-lg p-6 space-y-3">
        <p className="text-sm text-[#A1A1AA]">
          Email: <span className="text-white">{user.email}</span>
        </p>
        <div className="flex items-center gap-3">
          <span className="text-sm text-[#A1A1AA]">Admin:</span>
          <button
            type="button"
            onClick={requestRoleToggle}
            disabled={isSelf}
            title={isSelf ? "You can't change your own role" : ""}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              user.role === "admin" ? "bg-[#FF5500]" : "bg-white/10"
            } ${isSelf ? "opacity-40 cursor-not-allowed" : ""}`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                user.role === "admin" ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
          <span className="text-xs text-[#A1A1AA]">{user.role}</span>
        </div>
        <p className="text-sm text-[#A1A1AA]">
          Joined: {new Date(user.created_at).toLocaleDateString()}
        </p>
      </div>

      <h2 className="font-clash text-lg font-bold text-white">Licences ({licences.length})</h2>
      {licences.map((item) => (
        <div key={item.licence.licence_key} className="feature-card rounded-lg px-6 py-4">
          <code className="text-sm text-white font-mono break-all">{item.licence.licence_key}</code>
          <span className={`ml-4 text-xs ${item.licence.revoked ? "text-red-400" : "text-green-400"}`}>
            {item.licence.revoked ? "Revoked" : "Active"}
          </span>
          <span className="ml-4 text-xs text-[#A1A1AA]">
            {item.machines?.length || 0}/{item.licence.max_machines} machines
          </span>
        </div>
      ))}

      <h2 className="font-clash text-lg font-bold text-white">Orders ({orders.length})</h2>
      {orders.map((o) => (
        <div key={o._id} className="feature-card rounded-lg px-6 py-4 flex justify-between">
          <span className="text-sm text-white">${(o.amount_paid / 100).toFixed(2)}</span>
          <span className="text-xs text-[#A1A1AA]">
            {new Date(o.created_at).toLocaleDateString()}
          </span>
        </div>
      ))}

      {!isSelf && (
        <div className="border-t border-white/5 pt-6">
          <button
            type="button"
            onClick={() => { setShowDeleteModal(true); setConfirmEmail(""); setError(""); }}
            className="text-sm text-red-400 hover:text-red-300"
          >
            Delete user permanently
          </button>
        </div>
      )}

      {showRoleModal && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4"
          onClick={() => setShowRoleModal(false)}
        >
          <div className="feature-card rounded-lg p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-clash text-lg text-white mb-3">
              {pendingRole === "admin" ? "Grant admin access?" : "Revoke admin access?"}
            </h3>
            <p className="text-sm text-[#A1A1AA] mb-6">
              {pendingRole === "admin"
                ? `${user.email} will be able to delete users, manage licences, create coupons, and toggle other admins.`
                : `${user.email} will lose admin access on their next request.`}
            </p>
            {error && <p className="text-sm text-red-400 mb-3">{error}</p>}
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setShowRoleModal(false)}
                      className="px-4 py-2 text-sm text-[#A1A1AA] hover:text-white">Cancel</button>
              <button type="button" onClick={confirmRoleChange}
                      className="btn-primary px-4 py-2 rounded text-sm font-semibold">Confirm</button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4"
          onClick={() => setShowDeleteModal(false)}
        >
          <div className="feature-card rounded-lg p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-clash text-lg text-white mb-3">Delete {user.name}?</h3>
            <ul className="text-sm text-[#A1A1AA] space-y-2 mb-4 list-disc pl-5">
              <li>{licences.length} licence(s) will be revoked on every machine.</li>
              <li>{orders.length} order(s) will be retained but anonymized.</li>
              <li>Email and login credentials will be erased and cannot be recovered.</li>
            </ul>
            <p className="text-sm text-[#A1A1AA] mb-2">
              Type <code className="text-white">{user.email}</code> to confirm:
            </p>
            <input
              type="text"
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              className="w-full bg-[#1a1a1e] border border-white/10 rounded px-4 py-3 text-white mb-4 focus:border-red-500 focus:outline-none"
              placeholder={user.email}
              autoFocus
            />
            {error && <p className="text-sm text-red-400 mb-3">{error}</p>}
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setShowDeleteModal(false)}
                      className="px-4 py-2 text-sm text-[#A1A1AA] hover:text-white">Cancel</button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={confirmEmail !== user.email}
                className="px-4 py-2 rounded text-sm font-semibold bg-red-500 text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-red-600"
              >
                Delete permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
